<template>
  <table class="min-w-full">
    <thead class="bg-gray-50">
      <tr class="first-child:pl-3 last-child:pr-3 child:py-2 sticky top-0 text-xs child:border-b child:border-gray-200 font-semibold text-gray-600">
        <th class="text-left uppercase tracking-wider bg-gray-50">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.indirectCosts') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.budget') }}
        </th>

        <th
          v-if="claim?.capture_claim_amount"
          class="text-right uppercase tracking-wider bg-green-50"
        >
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.currentClaim') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.currentCertified') }}
        </th>

        <th
          v-if="claim?.capture_claim_amount"
          class="text-right uppercase tracking-wider bg-green-50"
        >
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.variance') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.previous') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50 pr-3">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.cumulative') }}
        </th>

        <th
          v-if="claim?.capture_claim_amount"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.currentClaimPercentage') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-blue-50">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.currentCertifiedPercentage') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-blue-50">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.previousPercentage') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-blue-50 pr-3">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.cumulativePercentage') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.costToComplete') }}
        </th>
      </tr>
    </thead>

    <tbody
      v-if="claim"
      class="bg-white divide-y divide-gray-200"
    >
      <TradeRow
        v-for="trade in claim.trades"
        :key="trade.id"
        :trade="trade"
        :claim="claim"
      />

      <!-- Grand Total Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-100 border-t-2 border-gray-300">
        <td class="font-bold text-gray-900">
          {{ $t('HeadContract.IndirectProgressClaimsShow.table.grandTotal') }}
        </td>

        <td class="text-right font-bold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.budget) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-bold text-gray-900"
        >
          {{ australianCurrency(totalCosts.trades.current_claim) }}
        </td>

        <td class="text-right font-bold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_certified) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-bold"
          :class="{ 'text-red-600': totalCosts.trades.variance !== 0, 'text-gray-900': totalCosts.trades.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.trades.variance) }}
        </td>

        <td class="text-right font-bold text-gray-900">
          {{ australianCurrency(totalCosts.trades.previous) }}
        </td>

        <td class="text-right font-bold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.cumulativeCertified) }}
        </td>

        <td
          v-if="claim.capture_claim_amount"
          class="text-right font-bold text-gray-900"
        >
          {{ totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.trades.current_claim) / (totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-gray-900">
          {{ totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.trades.current_certified) / (totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-gray-900">
          {{ totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.trades.previous) / (totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-gray-900 pr-3">
          {{ totalCosts.trades.budget > 0 ? australianNumber(((totalCosts.trades.cumulativeCertified) / (totalCosts.trades.budget)) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-gray-900">
          {{ australianCurrency(Math.abs(totalCosts.trades.budget - totalCosts.trades.cumulativeCertified)) }}
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber, isNumber } from '@/helpers';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { useClaimsStore } from '@/store/claims';
import { computed } from 'vue';
import TradeRow from './TradeRow.vue';

const props = defineProps<{
  claimId: string;
  projectId: string;
}>();

const claimsStore = useClaimsStore();

const claim = computed(() => {
  // TODO: 10/11/23 constrain variation orders to only those with cost for the current claim
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items')
          .where('boq_type', 'indirect')
          .whereHas('trade_item_costs')
          .orderBy('sort_order');
      })
      .with('trade_item_costs')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const totalCosts = computed(() => {
  const totals = {
    trades: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },
  };

  if(claim.value) {
    claim.value.trades?.forEach((trade) => {
      trade.trade_items.forEach((tradeItem) => {
        totals.trades.budget += tradeItem.budget;
        totals.trades.previous += tradeItem.previous(claim.value);
      });
    });

    claim.value.trade_item_costs.forEach((cost) => {
      totals.trades.current_claim += cost.current_claim || 0;
      totals.trades.current_certified += cost.current_certified;
      totals.trades.variance += cost.current_claim - cost.current_certified;
    });
  }

  totals.trades.cumulativeCertified = totals.trades.current_certified + totals.trades.previous;

  return totals;
});
</script>

<style scoped></style>
